import { CardContent } from '@src/components/Editor/types'
import {
  CardActionsPayload,
  CheckPayloadBase,
  CheckResultsError,
} from '@src/state/checks/actions'
import { CheckWithMetadata } from '@src/types/checks'
import { captureError } from '@src/utils/errors'
import { isDemoPage } from '@src/utils/routes'
import { getWordCount } from '@src/utils/wordCount'
import { RouterState } from 'connected-react-router'
import { EventDefinition } from 'redux-beacon'
import { getSelectedSchoolType, getSelectedUserType } from '../auth/selectors'
import {
  getPlainText,
  getSubjectTaxonomy,
  selectFreemiumPlagText,
} from '../document/selectors'
import {
  ONE_AUTH_MODAL,
  PAPER_SCORECARD_MODAL,
  PAPER_UPLOADED_MODAL,
  PAPER_UPLOADED_NON_PLUS_MODAL,
  PAYMENT_MODAL,
  REVIEWING_PAPER_MODAL,
  UPLOAD_WIDGET_MODAL,
} from '../modals/actions'
import { RootState } from '../types'
import {
  ClickstreamComponentViewPayload,
  ClickstreamInteractionPayload,
  ClickstreamNotificationPayload,
  ClickstreamSuccessPayload,
} from '../userevents/actions'
import { NewCardsPayload } from './actions'
import {
  CHECK_EVENT_NAMES,
  CHECK_EVENT_TYPES,
  EVENT_TYPES,
  FREEMIUM_PLAGIARISM,
  PAGE_TYPES,
  PAPER_UPLOAD_ID,
} from './constants'
import { getCharacterCount, getFileExtension, getPageNameFromPath } from './helpers'
import { EventWithConfig } from './types'
import { getInteractionElementName, getUpdatedView } from './utils'
import cardToCardEngagement, {
  cardToClickstreamInteraction,
} from './utils/cardInteractionToEvent'

export const locationChangeEvent: EventDefinition = (
  action,
  prevState
): EventWithConfig => {
  const location = (action.payload as RouterState).location
  const eventPayload = {
    type: EVENT_TYPES.CLICKSTREAM_VIEW,
    data: {},
  }
  if (location) {
    const { pathname } = location
    const view = getUpdatedView({
      site: prevState.site,
      page: getPageNameFromPath(pathname),
    })
    return {
      payload: eventPayload,
      config: {
        eventVersion: 3,
        stateUpdate: {
          view,
        },
      },
    }
  }
  return { payload: eventPayload, config: { eventVersion: 3 } }
}

export const paperReadyEvent: EventDefinition = (
  action,
  prevState: RootState,
  nextState: RootState
): EventWithConfig[] => {
  const { id, filename } = prevState.workspace
  const text = getPlainText(prevState)

  // TODO(WTF-5622): Remove paperReadyLegacyEvent once fully migrated to new clickstream success event
  const paperReadyLegacyEvent = {
    payload: {
      type: EVENT_TYPES.PAPER_READY,
      data: {
        state: 'ready' as const,
        upload_id: id,
        character_count: getCharacterCount(text),
        word_count: getWordCount(text),
        file_type: getFileExtension(filename),
      },
    },
    config: { eventVersion: 5 },
  }

  const paperReadyClickstreamEvent = clickstreamSuccessEvent(
    {
      payload: {
        name: CHECK_EVENT_NAMES.PROCESSED,
        content_id: id,
        content_label: PAPER_UPLOAD_ID,
        subjectTaxonomy: getSubjectTaxonomy(prevState),
        metadata: {
          state: CHECK_EVENT_TYPES.ready,
          upload_id: id,
          word_count: getWordCount(text),
          character_count: getCharacterCount(text),
          file_type: getFileExtension(filename),
          is_failed: false,
        },
      },
    },
    prevState,
    nextState
  )

  return [paperReadyLegacyEvent, paperReadyClickstreamEvent]
}
export const sendNewCardsEvent: EventDefinition = (
  action,
  prevState: RootState
): EventWithConfig[] => {
  const { newCards, gatedCards } = action.payload as NewCardsPayload
  const { id } = prevState.workspace
  const newCardEngagements = newCards.map(card => {
    return cardToCardEngagement(card, 'new', id)
  })

  const gatedCardEngagements = gatedCards.map(card => {
    return cardToCardEngagement(card, 'gated', id)
  })
  return [...newCardEngagements, ...gatedCardEngagements]
}

export const sendHiddenCardsEvent: EventDefinition = (
  action,
  prevState: RootState
): EventWithConfig[] => {
  const cards = action.payload as CardContent[]
  const { id } = prevState.workspace
  return cards.map(card => {
    return cardToCardEngagement(card, 'hidden', id)
  })
}

export const cardInteractionEvent: EventDefinition = (
  action,
  prevState: RootState,
  nextState: RootState
): EventWithConfig[] => {
  const { card, newState, selectedReplacement } = action.payload as CardActionsPayload
  const { id } = prevState.workspace
  // TODO(WTF-5623): Remove cardInteractionLegacyEvent once fully migrated to new clickstream interaction events
  const cardInteractionLegacyEvent = cardToCardEngagement(
    card,
    newState,
    id,
    selectedReplacement
  )

  const cardClickstreamInteractionEvent = cardToClickstreamInteraction(
    card,
    newState,
    id,
    prevState,
    nextState
  )

  return [cardInteractionLegacyEvent, cardClickstreamInteractionEvent]
}

export const paperCheckStartEvent: EventDefinition = (
  action,
  prevState: RootState,
  nextState: RootState
): EventWithConfig[] => {
  const { type, isRecheck } = action.payload as CheckPayloadBase
  const { id, filename } = prevState.workspace
  const text = getPlainText(prevState)

  // TODO(WTF-5622): Remove checkStartLegacyEvent once fully migrated to new clickstream success event
  const checkStartLegacyEvent = {
    payload: {
      type: EVENT_TYPES.PAPER_CHECK_START,
      data: {
        upload_id: id,
        character_count: getCharacterCount(text),
        word_count: getWordCount(text),
        file_type: getFileExtension(filename),
        state: CHECK_EVENT_TYPES.start,
        recheck: !!isRecheck,
        checkType: type,
      },
    },
    config: { eventVersion: 5 },
  }

  const checkStartClickstreamEvent = clickstreamSuccessEvent(
    {
      payload: {
        name: CHECK_EVENT_NAMES.PROCESSING,
        content_id: id,
        content_label: PAPER_UPLOAD_ID,
        subjectTaxonomy: getSubjectTaxonomy(prevState),
        metadata: {
          state: CHECK_EVENT_TYPES.start,
          check_type: type,
          upload_id: id,
          word_count: getWordCount(text),
          character_count: getCharacterCount(text),
          file_type: getFileExtension(filename),
          is_failed: false,
        },
      },
    },
    prevState,
    nextState
  )

  return [checkStartLegacyEvent, checkStartClickstreamEvent]
}

export const paperCheckEndEvent: EventDefinition = (
  action,
  prevState: RootState,
  nextState: RootState
): EventWithConfig[] => {
  const { type, status } = action.payload as CheckWithMetadata | CheckResultsError
  const { id, filename } = prevState.workspace
  const fullText = getPlainText(prevState)
  const text = type === FREEMIUM_PLAGIARISM ? selectFreemiumPlagText(prevState) : fullText

  // TODO(WTF-5622): Remove checkEndLegacyEvent once fully migrated to new clickstream success event
  const checkEndLegacyEvent = {
    payload: {
      type: EVENT_TYPES.PAPER_CHECK_END,
      data: {
        upload_id: id,
        character_count: getCharacterCount(text),
        word_count: getWordCount(text),
        file_type: getFileExtension(filename),
        state: CHECK_EVENT_TYPES.end,
        checkType: type,
        failed: status === 'FAILED',
      },
    },
    config: { eventVersion: 5 },
  }

  const checkEndClickstreamEvent = clickstreamSuccessEvent(
    {
      payload: {
        name: CHECK_EVENT_NAMES.PROCESSING,
        content_id: id,
        content_label: PAPER_UPLOAD_ID,
        subjectTaxonomy: getSubjectTaxonomy(prevState),
        metadata: {
          state: CHECK_EVENT_TYPES.end,
          check_type: type,
          upload_id: id,
          word_count: getWordCount(text),
          character_count: getCharacterCount(text),
          file_type: getFileExtension(filename),
          is_failed: status === 'FAILED',
        },
      },
    },
    prevState,
    nextState
  )

  return [checkEndLegacyEvent, checkEndClickstreamEvent]
}

export const partialPlagiarismCheckStartEvent: EventDefinition = (
  action,
  prevState: RootState,
  nextState: RootState
): EventWithConfig[] => {
  const { id, filename } = prevState.workspace
  const text = selectFreemiumPlagText(prevState)

  // TODO(WTF-5622): Remove freemiumPlagStartLegacyEvent once fully migrated to new clickstream success event
  const freemiumPlagStartLegacyEvent = {
    payload: {
      type: EVENT_TYPES.PAPER_CHECK_START,
      data: {
        upload_id: id,
        character_count: getCharacterCount(text),
        word_count: getWordCount(text),
        file_type: getFileExtension(filename),
        state: CHECK_EVENT_TYPES.start,
        recheck: false,
        checkType: FREEMIUM_PLAGIARISM,
      },
    },
    config: { eventVersion: 5 },
  }

  const freemiumPlagStartClickstreamEvent = clickstreamSuccessEvent(
    {
      payload: {
        name: CHECK_EVENT_NAMES.PROCESSING,
        content_id: id,
        content_label: PAPER_UPLOAD_ID,
        subjectTaxonomy: getSubjectTaxonomy(prevState),
        metadata: {
          state: CHECK_EVENT_TYPES.start,
          check_type: FREEMIUM_PLAGIARISM,
          upload_id: id,
          word_count: getWordCount(text),
          character_count: getCharacterCount(text),
          file_type: getFileExtension(filename),
          is_failed: false,
        },
      },
    },
    prevState,
    nextState
  )

  return [freemiumPlagStartLegacyEvent, freemiumPlagStartClickstreamEvent]
}

export const partialPlagiarismCheckEndEvent: EventDefinition = (
  action,
  prevState: RootState,
  nextState: RootState
): EventWithConfig[] => {
  const { id, filename } = prevState.workspace
  const text = selectFreemiumPlagText(prevState)

  // TODO(WTF-5622): Remove freemiumPlagEndLegacyEvent once fully migrated to new clickstream success event
  const freemiumPlagEndLegacyEvent = {
    payload: {
      type: EVENT_TYPES.PAPER_CHECK_END,
      data: {
        upload_id: id,
        character_count: getCharacterCount(text),
        word_count: getWordCount(text),
        file_type: getFileExtension(filename),
        state: CHECK_EVENT_TYPES.end,
        checkType: FREEMIUM_PLAGIARISM,
        failed: false,
      },
    },
    config: { eventVersion: 5 },
  }

  const freemiumPlagEndClickstreamEvent = clickstreamSuccessEvent(
    {
      payload: {
        name: CHECK_EVENT_NAMES.PROCESSING,
        content_id: id,
        content_label: PAPER_UPLOAD_ID,
        subjectTaxonomy: getSubjectTaxonomy(prevState),
        metadata: {
          state: CHECK_EVENT_TYPES.end,
          check_type: FREEMIUM_PLAGIARISM,
          upload_id: id,
          word_count: getWordCount(text),
          character_count: getCharacterCount(text),
          file_type: getFileExtension(filename),
          is_failed: false,
        },
      },
    },
    prevState,
    nextState
  )

  return [freemiumPlagEndLegacyEvent, freemiumPlagEndClickstreamEvent]
}

export const hideModalEvent: EventDefinition = (
  action,
  prevState: RootState
): EventWithConfig => {
  return {
    payload: {
      type: EVENT_TYPES.CLICKSTREAM_VIEW,
      data: {},
    },
    config: {
      eventVersion: 3,
      stateUpdate: {
        view: getUpdatedView({
          site: prevState.site,
          page: getPageNameFromPath(prevState.router.location.pathname),
        }),
      },
    },
  }
}

export const showModalEvent: EventDefinition = (
  action,
  prevState: RootState
): EventWithConfig => {
  const { pathname } = prevState.router.location
  const { name, modalProps } = action.payload
  let viewName

  switch (name) {
    case REVIEWING_PAPER_MODAL:
      viewName = PAGE_TYPES.PAPER_PROCESSING_MODAL
      break
    case PAPER_UPLOADED_MODAL:
      viewName = PAGE_TYPES.RESULTS_MODAL
      break
    case PAPER_UPLOADED_NON_PLUS_MODAL:
      viewName = PAGE_TYPES.UPSELL_MODAL
      break
    case PAYMENT_MODAL:
      viewName = PAGE_TYPES.PAYWALL_MODAL
      break
    case 'PLAGIARISM_SOURCES_MODAL':
      viewName = PAGE_TYPES.PLAGIARISM_SOURCES_MODAL
      break
    case 'PLAGIARISM_CHECK_MODAL':
      viewName = PAGE_TYPES.PLAGIARISM_CHECK_MODAL
      break
    case 'CHECK_FAILED_MODAL':
      viewName = PAGE_TYPES.CHECK_FAILED_MODAL
      break
    case isDemoPage(pathname) && PAPER_SCORECARD_MODAL:
      viewName = PAGE_TYPES.PAPER_SCORECARD_MODAL_DEMO
      break
    case PAPER_SCORECARD_MODAL:
      viewName = PAGE_TYPES.PAPER_SCORECARD_MODAL
      break
    case UPLOAD_WIDGET_MODAL:
      viewName = modalProps?.isResubmit
        ? PAGE_TYPES.UPLOAD_WIDGET_RESUBMIT_MODAL
        : PAGE_TYPES.UPLOAD_WIDGET_MODAL
      break
    case ONE_AUTH_MODAL:
      viewName = PAGE_TYPES.AUTH_MODAL
      break
    default:
      captureError(new Error(`${name} is not a valid modal type`))
      viewName = name
  }

  return {
    payload: {
      type: EVENT_TYPES.CLICKSTREAM_VIEW,
      data: {},
    },
    config: {
      eventVersion: 3,
      stateUpdate: {
        view: getUpdatedView({ site: prevState.site, page: viewName }),
      },
    },
  }
}

export const paymentSuccessEvent: EventDefinition = (
  action,
  prevState: RootState
): EventWithConfig => ({
  payload: {
    type: EVENT_TYPES.CLICKSTREAM_VIEW,
    data: {},
  },
  config: {
    eventVersion: 3,
    stateUpdate: {
      view: getUpdatedView({
        site: prevState.site,
        page: PAGE_TYPES.PAYMENT_CONFIRMATION_MODAL,
      }),
    },
  },
})

export const showCheggAuthModalEvent: EventDefinition = (
  action,
  prevState
): EventWithConfig => {
  return {
    payload: {
      type: EVENT_TYPES.CLICKSTREAM_VIEW,
      data: {},
    },
    config: {
      eventVersion: 3,
      stateUpdate: {
        view: getUpdatedView({ site: prevState.site, page: PAGE_TYPES.AUTH_MODAL }),
      },
    },
  }
}

export const clickstreamInteractionEvent: EventDefinition = (action): EventWithConfig => {
  const {
    element,
    origin,
    region,
    text,
    type,
    value,
    interactionType,
    metadata,
  } = action.payload as ClickstreamInteractionPayload
  const name = getInteractionElementName({ element, origin })

  return {
    payload: {
      type: EVENT_TYPES.CLICKSTREAM_INTERACTION,
      data: {
        interaction: {
          element: {
            name,
            region_name: region,
            text,
            type,
            value,
          },
          type: interactionType,
        },
        content: {
          metadata,
        },
      },
    },
    config: { eventVersion: 3 },
  }
}

export const clickstreamNotificationsEvent: EventDefinition = (
  action
): EventWithConfig => {
  const { message, type }: ClickstreamNotificationPayload = action.payload
  return {
    payload: {
      type: EVENT_TYPES.CLICKSTREAM_NOTIFICATIONS,
      data: {
        notifications: {
          message_name: message,
          type,
        },
      },
    },
    config: { eventVersion: 3 },
  }
}

export const clickStreamSignUpStartEvent: EventDefinition = (
  action,
  prevState: RootState
): EventWithConfig => ({
  payload: {
    type: EVENT_TYPES.CLICKSTREAM_SIGN_UP_START,
    data: {},
  },
  config: { eventVersion: 3 },
})

export const clickStreamSignUpSuccessEvent: EventDefinition = (
  action,
  prevState: RootState
): EventWithConfig => {
  const { authService } = action.payload
  return {
    payload: {
      type: EVENT_TYPES.CLICKSTREAM_SIGN_UP_SUCCESS,
      data: {
        sign_up: {
          auth_service: authService,
          member_role: getSelectedUserType(prevState)!,
          school_type: getSelectedSchoolType(prevState),
        },
      },
    },
    config: { eventVersion: 3 },
  }
}

export const clickStreamSignInStartEvent: EventDefinition = (
  action,
  prevState: RootState
): EventWithConfig => {
  return {
    payload: {
      type: EVENT_TYPES.CLICKSTREAM_SIGN_IN_START,
      data: {},
    },
    config: { eventVersion: 3 },
  }
}

export const clickStreamSignInSuccessEvent: EventDefinition = (
  action,
  prevState: RootState
): EventWithConfig => {
  return {
    payload: {
      type: EVENT_TYPES.CLICKSTREAM_SIGN_IN_SUCCESS,
      data: {
        sign_in: {
          auth_service: action.payload.authService,
        },
      },
    },
    config: { eventVersion: 3 },
  }
}

export const clickstreamComponentViewEvent: EventDefinition = (
  action: { payload: ClickstreamComponentViewPayload },
  prevState: RootState
): EventWithConfig => {
  const { name, value, text, index } = action.payload
  return {
    payload: {
      type: EVENT_TYPES.CLICKSTREAM_COMPONENT_VIEW,
      data: {
        component_view: {
          component: {
            type: 'component',
            name,
            value,
            text,
            index,
          },
        },
      },
    },
    config: {
      eventVersion: 3,
    },
  }
}

export const clickstreamSuccessEvent: EventDefinition<
  EventWithConfig,
  { payload: ClickstreamSuccessPayload }
> = action => {
  const {
    name,
    metadata,
    subjectTaxonomy,
    content_id,
    content_label,
    state,
  } = action.payload

  return {
    payload: {
      type: EVENT_TYPES.CLICKSTREAM_SUCCESS,
      data: {
        name,
        state,
        content: {
          content_id,
          content_label,
          taxonomy: {
            subjects: subjectTaxonomy,
          },
          metadata: { wt: { writing: metadata } },
        },
      },
    },
    config: {
      eventVersion: 3,
    },
  }
}



// WEBPACK FOOTER //
// ./src/state/analytics/definitions.ts